import { Component, ChangeDetectionStrategy, OnChanges, SimpleChanges, input, output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { ExpansionPanelModule } from '@progress/kendo-angular-layout';
import { cueCheck, cueClose, cueArrowFullLeft, IconComponent } from '@cue/ui/icons';
import { LocalizedStringPipe } from '@cue/utilities';
import { QRCategoryItemModel } from '@cue/api';
import { AreaTreeComponent } from '@cue/assist/ui/area-tree';
import { ButtonComponent, ToggleButtonComponent } from '@cue/ui';
import { translation } from '@cue/translations';

export interface TargetFilterData {
  selectedCategoryIdList: string[];
  selectedResourceTypeIdList: number[];
  selectedAreaIdList: number[];
}

@Component({
  selector: 'cue-start-target-filter',
  templateUrl: './start-target-filter.component.html',
  styleUrls: ['./start-target-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ToggleButtonComponent,
    ExpansionPanelModule,
    TranslocoPipe,
    ReactiveFormsModule,
    TextBoxModule,
    IconComponent,
    ButtonComponent,
    AreaTreeComponent,
    LocalizedStringPipe,
  ],
})
export class StartTargetFilterComponent implements OnChanges {
  protected ICONS = {
    cueCheck,
    cueClose,
    cueArrowFullLeft,
  };
  protected readonly translation = translation;

  currentFilter = input<TargetFilterData>({
    selectedCategoryIdList: [],
    selectedResourceTypeIdList: [],
    selectedAreaIdList: [],
  });

  selectedCategoryIdList = input<string[]>();
  selectedResourceTypeIdList = input<number[]>();
  selectedAreaIdList = input<number[]>();

  isKioskMode = input(false);

  availableCategoryList = input<QRCategoryItemModel[]>();
  availableAreaList = input<any[]>();
  requestClose = output<void>();
  confirmFilter = output<TargetFilterData>();

  protected onlyCategoryItemList: QRCategoryItemModel[];
  protected onlyResourceItemList: QRCategoryItemModel[];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCategoryIdList'] && this.selectedCategoryIdList()) {
      this.currentFilter().selectedCategoryIdList = this.selectedCategoryIdList();
    }
    if (changes['selectedResourceTypeIdList'] && this.selectedResourceTypeIdList()) {
      this.currentFilter().selectedResourceTypeIdList = this.selectedResourceTypeIdList();
    }
    if (changes['selectedAreaIdList'] && this.selectedAreaIdList()) {
      this.currentFilter().selectedAreaIdList = this.selectedAreaIdList();
    }

    if (changes['availableCategoryList'] && this.availableCategoryList()?.length > 0) {
      this.onlyCategoryItemList = this.availableCategoryList().filter((q) => !q.resourceTypeId);
      this.onlyResourceItemList = this.availableCategoryList().filter((q) => q.resourceTypeId);
    }
  }

  protected addRemoveCategoryFromFilter(categoryId: string) {
    const indexOfSelectedItem = this.getSelectedCategoryIndex(categoryId);
    if (indexOfSelectedItem > -1) {
      this.currentFilter().selectedCategoryIdList = this.currentFilter().selectedCategoryIdList.filter(
        (x) => x != categoryId
      );
    } else {
      this.currentFilter().selectedCategoryIdList.push(categoryId);
    }

    this.confirmFilter.emit(this.currentFilter());
  }

  protected addRemoveResourceFromFilter(resourceTypeId: number) {
    const indexOfSelectedItem = this.getSelectedResourceIndex(resourceTypeId);
    if (indexOfSelectedItem > -1) {
      this.currentFilter().selectedResourceTypeIdList = this.currentFilter().selectedResourceTypeIdList.filter(
        (x) => x != resourceTypeId
      );
    } else {
      this.currentFilter().selectedResourceTypeIdList.push(resourceTypeId);
    }
    this.confirmFilter.emit(this.currentFilter());
  }

  protected getSelectedCategoryIndex(categoryId: string) {
    return this.currentFilter().selectedCategoryIdList.findIndex((x) => x == categoryId);
  }

  protected getSelectedResourceIndex(resourceTypeId: number) {
    return this.currentFilter().selectedResourceTypeIdList.findIndex((x) => x == resourceTypeId);
  }

  selectedAreaIdChanged(data: any) {
    this.currentFilter().selectedAreaIdList = data;
    this.confirmFilter.emit(this.currentFilter());
  }

  protected clearTypeFilter() {
    this.currentFilter().selectedResourceTypeIdList = [];
    this.confirmFilter.emit(this.currentFilter());
  }

  protected clearCategoryFilter() {
    this.currentFilter().selectedCategoryIdList = [];
    this.confirmFilter.emit(this.currentFilter());
  }

  protected clearAreaFilter() {
    this.currentFilter().selectedAreaIdList = [];
    this.confirmFilter.emit(this.currentFilter());
  }
}
